import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, faqs, comics, categories } from '@/mocks/comics';

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const payload = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9on652ftmtnfcfh0a8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    name: 'UU漫画',
    url: 'https://uumanhua.com',
    description: 'UU漫画提供漫画追更、最新漫画更新及连载漫画同步阅读，追漫必备的更新提醒功能，免费漫画全集随时看。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@uumanhua.com',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchemaItems = faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  }));

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            { '@context': 'https://schema.org', '@type': 'Organization', ...orgSchema },
            { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqSchemaItems },
          ]),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Intro */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-5">
                关于 UU漫画
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                UU漫画成立于 2024 年，是专注于漫画追更和连载漫画同步阅读的综合性漫画平台。
                我们致力于为漫画爱好者提供最新漫画更新信息、完善的更新提醒功能和丰富的免费漫画全集资源，
                让每一位漫迷都能享受追更漫画的乐趣，不再错过任何精彩章节。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-8 py-12 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: `${comics.length}+`, label: '热门漫画' },
                  { value: `${categories.length}+`, label: '分类齐全' },
                  { value: '1000万+', label: '漫画爱好者' },
                  { value: '99.9%', label: '好评率' },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Our Mission */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-rocket-line',
                    title: '追漫更及时',
                    desc: '漫画更新提醒功能，第一时间推送最新漫画更新通知，让您的追漫体验更加高效便捷。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '内容更全面',
                    desc: '涵盖热血、恋爱、悬疑、奇幻、科幻等多种漫画分类，免费漫画全集随时阅读。',
                  },
                  {
                    icon: 'ri-user-heart-line',
                    title: '体验更美好',
                    desc: '简洁清爽的界面设计，流畅的阅读体验，致力于为漫画爱好者打造最好的追漫平台。',
                  },
                ].map((item, idx) => (
                  <div key={idx} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-lg text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-2">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                来自漫画爱好者对UU漫画的真实评价
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-xs text-primary-400"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？欢迎通过以下表单与我们取得联系
              </p>
              <form
                data-readdy-form=""
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-form-extra"
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>

                {formError && (
                  <p className="text-sm text-red-500 bg-red-50 px-4 py-2 rounded-lg">{formError}</p>
                )}

                {formStatus === 'success' ? (
                  <div className="text-center py-3 bg-accent-50 text-accent-700 rounded-lg text-sm font-medium">
                    提交成功！感谢您的留言，我们会尽快回复。
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交留言'}
                  </button>
                )}
              </form>
            </div>
          </section>

          {/* Contact Info */}
          <section className="w-full px-4 md:px-8 py-10 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@uumanhua.com' },
                  { icon: 'ri-qq-line', title: 'QQ交流群', value: '888888888' },
                  { icon: 'ri-time-line', title: '工作时间', value: '周一至周五 9:00-18:00' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{item.title}</p>
                      <p className="text-sm font-medium text-foreground-800 truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-8 py-14 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, idx) => (
                  <details key={idx} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}